/**
 * Super Admin Signup Page
 * Request super admin access
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import api from '../utils/api';

const SuperAdminSignup = () => {
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        email: '',
        name: '',
        password: '',
        confirmPassword: '',
    });

    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (error) setError('');
    };

    const validateEmail = (email) => {
        // Bounded quantifiers to prevent ReDoS - max 64 chars local, 255 domain
        const emailRegex = /^[^\s@]{1,64}@[^\s@]{1,255}\.[^\s@]{1,63}$/;
        return emailRegex.test(email);
    };

    const handleSignup = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess(false);

        // Validation
        if (!formData.email || !formData.name || !formData.password || !formData.confirmPassword) {
            setError('Please fill in all required fields');
            return;
        }

        if (!validateEmail(formData.email)) {
            setError('Please enter a valid email address');
            return;
        }

        if (formData.password.length < 8) {
            setError('Password must be at least 8 characters long');
            return;
        }

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await api.post('/api/auth/super-admin/signup', {
                email: formData.email,
                name: formData.name,
                password: formData.password,
            });

            if (response.data.success) {
                setSuccess(true);
                // Redirect to login after 2 seconds
                setTimeout(() => {
                    navigate('/super-admin/login', {
                        state: {
                            message: 'Super admin account created successfully! Please log in.',
                            email: formData.email
                        }
                    });
                }, 2000);
            } else {
                setError(response.data.error || 'Signup failed');
            }
        } catch (err) {
            const errorMessage = err.response?.data?.detail?.message
                || err.response?.data?.detail
                || 'An error occurred during signup';
            setError(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-indigo-100 p-4">
            <Card className="w-full max-w-md border-purple-200">
                <CardHeader className="space-y-1">
                    <CardTitle className="text-2xl font-bold text-center text-purple-800">
                        Super Admin Signup
                    </CardTitle>
                    <CardDescription className="text-center">
                        Enter your details to create a super admin account
                    </CardDescription>
                </CardHeader>

                <CardContent>
                    <form onSubmit={handleSignup} className="space-y-4">
                        {error && (
                            <Alert variant="destructive">
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )}

                        {success && (
                            <Alert className="bg-green-50 text-green-900 border-green-200">
                                <AlertDescription>
                                    Super admin account created successfully! Redirecting to login...
                                </AlertDescription>
                            </Alert>
                        )}

                        <div className="space-y-2">
                            <Label htmlFor="email">Email Address *</Label>
                            <Input
                                id="email"
                                name="email"
                                type="email"
                                placeholder="your-email@example.com"
                                value={formData.email}
                                onChange={handleChange}
                                disabled={isSubmitting || success}
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="name">Full Name *</Label>
                            <Input
                                id="name"
                                name="name"
                                type="text"
                                placeholder="John Doe"
                                value={formData.name}
                                onChange={handleChange}
                                disabled={isSubmitting || success}
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password">Password *</Label>
                            <Input
                                id="password"
                                name="password"
                                type="password"
                                placeholder="Min. 8 characters"
                                value={formData.password}
                                onChange={handleChange}
                                disabled={isSubmitting || success}
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword">Confirm Password *</Label>
                            <Input
                                id="confirmPassword"
                                name="confirmPassword"
                                type="password"
                                placeholder="Confirm your password"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                disabled={isSubmitting || success}
                                required
                            />
                        </div>

                        <Button
                            type="submit"
                            className="w-full bg-purple-600 hover:bg-purple-700"
                            disabled={isSubmitting || success}
                        >
                            {isSubmitting ? 'Creating account...' : 'Create Account'}
                        </Button>
                    </form>
                </CardContent>

                <CardFooter className="flex flex-col space-y-4">
                    <div className="text-sm text-center text-gray-600">
                        Already have super admin credentials?{' '}
                        <Link
                            to="/super-admin/login"
                            className="text-purple-600 hover:text-purple-700 hover:underline font-medium"
                        >
                            Sign in
                        </Link>
                    </div>

                    <div className="text-sm text-center text-gray-600">
                        Regular user?{' '}
                        <Link
                            to="/login"
                            className="text-blue-600 hover:text-blue-700 hover:underline font-medium"
                        >
                            Go to login
                        </Link>
                    </div>
                </CardFooter>
            </Card>
        </div>
    );
};

export default SuperAdminSignup;
